import { useContent } from '../../contexts/ContentContext'
import CtaButton from '../ui/CtaButton'
import {
  resolveSectionTone,
  sectionTextClasses,
  resolveImageOverlay,
} from '../../utils/sectionTone'
import HeroDualPathAnimation from './HeroDualPathAnimation'

/**
 * HeroImageSection — full-bleed image hero (reusable).
 * Content: heroImage { backgroundUrl, dualPaths? | headline+pathWow+pathOkay, cta? }
 * Dual-path Motion loop when dualPaths (or legacy pathWow+pathOkay) present.
 * Exactly one CTA (bottom) — no topCta.
 */
export default function HeroImageSection() {
  const { heroImage, fullBleedHero } = useContent()
  const data = heroImage || fullBleedHero
  if (!data) return null

  const { backgroundUrl, headline, pathWow, pathOkay, dualPaths, cta } = data
  const tone = resolveSectionTone({
    ...data,
    surface: data.surface || (backgroundUrl ? 'image' : undefined),
  })
  const t = sectionTextClasses(tone)
  const imageOverlay = resolveImageOverlay(
    {
      ...data,
      overlayOpacity: data.overlayOpacity ?? (data.overlay ? undefined : 0.55),
      overlayColor: data.overlayColor || 'navy',
    },
    tone
  )

  const dualPath = Boolean(
    (Array.isArray(dualPaths) && dualPaths.length >= 2) || (pathWow && pathOkay)
  )

  return (
    <section
      id={data.id || 'hero'}
      data-section="HeroImageSection"
      data-testid="hero-image-section"
      data-tone={tone}
      className="relative min-h-[100svh] snap-section flex flex-col justify-center overflow-hidden"
    >
      {backgroundUrl ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${backgroundUrl})` }}
            aria-hidden="true"
          />
          <div
            className={`absolute inset-0 ${imageOverlay.className || ''}`}
            style={imageOverlay.style}
            aria-hidden="true"
            data-testid="hero-image-overlay"
          />
        </>
      ) : (
        <div
          className={`absolute inset-0 ${t.onDark ? 'bg-color-primary' : 'bg-color-bg-light'}`}
          aria-hidden="true"
        />
      )}

      {/* overflow-visible so drop-shadow blur is not sheared by text stack */}
      <div className="relative z-10 w-full max-w-content mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-28 overflow-visible">
        {dualPath ? (
          <HeroDualPathAnimation
            dualPaths={dualPaths}
            headline={headline}
            pathWow={pathWow}
            pathOkay={pathOkay}
            headlineClass={t.display}
            accentClass="text-color-highlight"
            lineClass={t.sub}
          />
        ) : (
          <>
            {headline ? (
              <h1 className={`${t.display} leading-tight max-w-3xl`}>{headline}</h1>
            ) : null}
            {pathWow ? (
              <p className={`mt-8 md:mt-10 ${t.sub} text-color-highlight leading-snug max-w-2xl`}>
                {pathWow}
              </p>
            ) : null}
          </>
        )}

        {cta ? (
          <div className="mt-10 md:mt-12" data-testid="hero-cta">
            <CtaButton label={cta} variant="primary" onDark={t.onDark} className="py-4 px-10" />
          </div>
        ) : null}
      </div>
    </section>
  )
}
